import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const ScenarioChart = ({ result }) => {
  const scenarios = [
    {
      name: 'Conservative',
      value: parseFloat(result.futureValueMin),
      color: '#6B7280',
      icon: TrendingDown,
      rate: result.scenario.value === 'conservative' ? result.annualRate : '5'
    },
    {
      name: 'Base',
      value: parseFloat(result.futureValueBase || result.futureValueSelected),
      color: '#D4AF37',
      icon: Minus,
      rate: result.scenario.value === 'base' ? result.annualRate : '12'
    },
    {
      name: 'Optimistic',
      value: parseFloat(result.futureValueMax),
      color: '#10B981',
      icon: TrendingUp,
      rate: result.scenario.value === 'optimistic' ? result.annualRate : '20'
    }
  ];

  const maxValue = Math.max(...scenarios.map(s => s.value));
  const initialValue = result.investmentAmount;

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-gray-400 mb-4">Scenario Comparison</h4>
      
      {/* Investment baseline */}
      <div className="relative mb-6">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
          <span>Initial Investment</span>
          <span>{result.currency.symbol}{initialValue.toLocaleString()}</span>
        </div>
        <div className="h-2 bg-[#1e1e2e] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gray-600 rounded-full"
            style={{ width: `${(initialValue / maxValue) * 100}%` }}
          />
        </div>
      </div>

      {/* Scenario bars */}
      {scenarios.map((scenario, index) => {
        const Icon = scenario.icon;
        const percentage = ((scenario.value - initialValue) / initialValue * 100).toFixed(1);
        const isSelected = result.scenario.label.toLowerCase() === scenario.name.toLowerCase();
        
        return (
          <motion.div
            key={scenario.name}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 + 0.3 }}
            className={`relative p-3 rounded-xl transition-all ${
              isSelected 
                ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' 
                : 'bg-[#0a0a0f] border border-transparent hover:border-[#1e1e2e]'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${scenario.color}20` }}
                >
                  <Icon className="w-4 h-4" style={{ color: scenario.color }} />
                </div>
                <div>
                  <span className="text-sm font-medium text-white">{scenario.name}</span>
                  {isSelected && (
                    <span className="ml-2 text-xs px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full">
                      Selected
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-white">
                  {result.currency.symbol}{scenario.value.toLocaleString()}
                </p>
                <p className="text-xs" style={{ color: scenario.color }}>
                  +{percentage}%
                </p>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-[#1e1e2e] rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: scenario.color }}
                initial={{ width: 0 }}
                animate={{ width: `${(scenario.value / maxValue) * 100}%` }}
                transition={{ duration: 0.8, delay: index * 0.1 + 0.5, ease: "easeOut" }}
              />
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ScenarioChart;
